import { Injectable } from '@angular/core';

import { AppState } from './store/AppState';
import { PlycaDataService } from '../services/plyca.service';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { ServiceLocator } from './ServiceLocator';
import { ProfileDTO, UserDTO } from './api/plyca-dtos';
import { SessionInfo, UserPrefs } from './api/UserPrefs-dto';
import { UIService } from './ui.service';

import { TranslateService } from '@ngx-translate/core';
import { BaseDirectiveObject } from './components/common/base-directive-object';
import { UserPrefsService } from './store/user-prefs.service';
import { REMOVE_ALLUSEREXTRAPARAMS, SET_USEREXTRAPARAM } from './store/user';

@Injectable()
export class UserService extends BaseDirectiveObject {
  private store: Store<AppState>;
  public plycaService: PlycaDataService;
  private router: Router;
  private uiService: UIService;
  private translate: TranslateService;
  sessionInfo: SessionInfo;
  userPrefs: UserPrefs;
  userLetters: string;
  userColor: string;
  userFullName: string;

  static calculateUserFullName(user1: UserDTO) {
    let text = ((user1) ? '[' + user1.identifier + ']' : 'NA');
    if (user1 && user1.identifier && user1.identifier === 'AUTOMATICO') {
      return 'Automático';
    }
    if (user1 && user1.person) {
      if (user1.person.name) {
        text = user1.person.name;
      }
      if (user1.person.surname) {
        text = text + ' ' + user1.person.surname;
      }
      if (user1.person.lastName) {
        text = text + ' ' + user1.person.lastName;
      }
    }
    return text;
  }

  constructor(
    private userPrefsService: UserPrefsService) {
    super();
    this.store = ServiceLocator.injector.get(Store) as Store<AppState>;
    this.plycaService = ServiceLocator.injector.get(PlycaDataService);
    this.router = ServiceLocator.injector.get(Router);
    this.uiService = ServiceLocator.injector.get(UIService);
    this.translate = ServiceLocator.injector.get(TranslateService);
    this.getStoreUser();
  }

  private getStoreUser() {
    this.subscribe(this.store.select('user'), (sesInfo: SessionInfo) => {
      this.sessionInfo = sesInfo;
      this.userLetters = this.calculateLetters();
      this.userColor = this.calculateColor();
      this.userFullName = this.calculateFullName();
    });
    this.subscribe(this.userPrefsService.getUserPrefs(), (prefs: UserPrefs) => {
      this.userPrefs = prefs;
    });
  }

  public getUserFullName() {
    return this.userFullName;
  }

  public getUserFullNameSelectedUser(user: UserDTO) {
    return this.calculateUserFullName(user);
  }

  public getUserLetters() {
    return this.userLetters;
  }

  public getUserLettersSelectedUser(user: UserDTO) {
    return this.calculateUserLetters(user);
  }

  public getUserColor() {
    return this.userColor;
  }

  public getUserColorSelectedUser(user: UserDTO) {
    return this.calculateUserColor(user);
  }

  public getUserId() {
    if (this.sessionInfo && this.sessionInfo.user) {
      return this.sessionInfo.user.identifier;
    }
    return '';
  }

  public calculateUserFullName(user1: UserDTO) {
    return UserService.calculateUserFullName(user1);
  }

  private calculateFullName() {
    if (this.sessionInfo) {
      return this.calculateUserFullName(this.sessionInfo.user);
    }
    return '';
  }

  public calculateUserLetters(user1: UserDTO): string {
    let txt = 'ND';
    if (user1 && user1.person) {
      if (user1.person && user1.person.name && user1.person.surname) {
        txt =
          user1.person.name.slice(0, 1).toUpperCase() +
          user1.person.surname.slice(0, 1).toUpperCase();
      }
    }
    return txt;
  }

  private calculateLetters(): string {
    if (this.sessionInfo) {
      return this.calculateUserLetters(this.sessionInfo.user);
    }
    return '';
  }

  public calculateUserColor(user1: UserDTO): string {
    let alphabet = [
      'A',
      'B',
      'C',
      'D',
      'E',
      'F',
      'G',
      'H',
      'I',
      'J',
      'K',
      'L',
      'M',
      'N',
      'O',
      'P',
      'Q',
      'R',
      'S',
      'T',
      'U',
      'V',
      'W',
      'X',
      'Y',
      'Z'
    ];
    let letter = 'N';
    if (user1 && user1.person) {
      if (user1.person && user1.person.name) {
        letter = user1.person.name.slice(0, 1).toUpperCase();
      }
    }
    return this.uiService.USERS_COLOR_LIST[alphabet.indexOf(letter)];
  }

  private calculateColor(): string {
    if (this.sessionInfo) {
      return this.calculateUserColor(this.sessionInfo.user);
    }
    return '';
  }

  public getSystemColor() {
    return this.uiService.USERS_COLOR_LIST[5];
  }

  public getSystemLetters() {
    return 'PL';
  }

  public getSystemFullName() {
    return 'Sistema PLYCA';
  }

  public getRamdomColor() {
    return this.uiService.USERS_COLOR_LIST[this.getRandomInt(0, 10)];
  }

  getRandomInt(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  getUser(): UserDTO {
    let user: UserDTO;
    if (this.sessionInfo) {
      user = this.sessionInfo.user;
    }
    return user;
  }

  getModoAcceso(): boolean {
    if (this.sessionInfo) {
      return this.sessionInfo.modoAcceso;
    }
  }

  hasInbox(inbox: string): boolean {
    return (this.sessionInfo.user?.inboxes.find(i => i.identifier == inbox) == undefined);
  }

  userEntity() {
    if (this.sessionInfo.user.organizationalUnit.entity == false && this.sessionInfo.user.organizationalUnit.entityDTO) {
      return this.sessionInfo.user.organizationalUnit?.entityDTO;
    } else if (this.sessionInfo.user.organizationalUnit.entity == true) {
      return this.sessionInfo.user.organizationalUnit;
    } else {
      return null;
    }
  }

  hasModulesAccess(modules: string[]) {
    if (!modules || !this.userEntity().activeModules) {
      return false;
    }
    let activeModules = this.userEntity().activeModules.filter(x => x.active && modules.filter(y => y == x.identifier).length > 0);
    return activeModules.length > 0;
  }

  hasRoles(roles: string, app?: string) {
    if (!roles) {
      return false;
    }
    // si la lista de roles viene con * devolvemos true
    if (roles === '*') {
      return true;
    }
    let separator = ',';
    if (roles.indexOf(separator) < 0) {
      separator = ';';
    }
    let ret = false;
    let perIds = roles.split(separator);
    if (
      perIds &&
      this.sessionInfo &&
      this.sessionInfo.user &&
      this.sessionInfo.user.listProfiles
    ) {
      perIds.forEach(roleId => {
        let obj = this.sessionInfo.user.listProfiles.find(
          profile => profile.identifier == roleId.trim() && (!app || app === profile.application)
          // {
          // if (app) {
          //   return profile.application === app && profile.identifier == roleId.trim();;
          // } else {
          //   return profile.identifier == roleId.trim();
          // }
          //   return profile.identifier == roleId.trim() && (!app || profile.application === app);
          // }
        );
        ret = ret || obj !== undefined;
      });
    }
    return ret;
  }


  hasRolesInApp(app: string) {
    let ret = false;
    if (
      this.sessionInfo &&
      this.sessionInfo.user &&
      this.sessionInfo.user.listProfiles
    ) {
      // comprobamos si el usuario tiene en su lista de perfiles un perfil en el aplicativo
      this.sessionInfo.user.listProfiles.forEach((profile: ProfileDTO) => {
        if (profile.application === app) {
          ret = true;
        }
      });
    }
    return ret;
  }

  public getUserPreferences(): UserPrefs {
    return this.userPrefs;
  }

  public setUserExtraParams(paramKey: string, paramValue: any) {
    this.store.dispatch(SET_USEREXTRAPARAM({ key: paramKey, value: paramValue }));
  }

  public removeUserExtraParam(paramKey: string): void {
    this.store.dispatch(SET_USEREXTRAPARAM({ key: paramKey, value: undefined }));
  }

  public removeAllUserExtraParam(): void {
    this.store.dispatch(REMOVE_ALLUSEREXTRAPARAMS({}));
  }

  public getSessionInfo(): SessionInfo {
    return this.sessionInfo;
  }

}
